/**
 * SSR-safe debounce wrapper
 *
 * На клиенте: работает как обычный debounce
 * На сервере: вызывает функцию сразу без задержки
 *
 * @example
 * ```typescript
 * const debouncedFn = ssrSafeDebounce(() => {
 *   console.log('Called')
 * }, 300)
 *
 * debouncedFn() // На клиенте: вызов через 300ms, на сервере: сразу
 * ```
 */

import type {DebounceSettings} from 'lodash-es';
import debounce from 'lodash-es/debounce';

type DebouncedFunc<T extends (...args: any[]) => any> = {
    (...args: Parameters<T>): ReturnType<T> | undefined;
    cancel(): void;
    flush(): ReturnType<T> | undefined;
};

/**
 * SSR-safe debounce
 *
 * @param func - Функция для debounce
 * @param wait - Задержка в миллисекундах (игнорируется на сервере)
 * @param options - Опции debounce (игнорируются на сервере)
 * @returns Debounced функция на клиенте, обычная функция на сервере
 */
export function ssrSafeDebounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number = 0,
    options?: DebounceSettings
): DebouncedFunc<T> {
    // На сервере возвращаем обертку которая вызывает функцию сразу
    if (process.env.SERVER) {
        const immediateFunc = ((...args: Parameters<T>) => {
            return func(...args);
        }) as DebouncedFunc<T>;

        // Добавляем методы для совместимости с debounced функцией
        immediateFunc.cancel = () => {
        };
        immediateFunc.flush = () => undefined;

        return immediateFunc;
    }

    // На клиенте используем обычный debounce
    return debounce(func, wait, options) as DebouncedFunc<T>;
}
